import type {
  LangChainMessage,
  LangGraphMessagesEvent
} from "@assistant-ui/react-langgraph";

const eventType = (event: string) => event.split("|", 1)[0];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Whether a node-output message is an assistant (AI) message.
 *
 * Only these are the duplicate reply bubble; tool/human/system messages carried
 * on the same `updates` frame (e.g. a tools node's `ToolMessage` results) are
 * legitimate and must be preserved.
 * @param message - A serialized message from a node output.
 * @returns True for AI/assistant messages (including streamed chunks).
 */
const isAiMessage = (message: unknown): boolean => {
  if (!isRecord(message)) return false;
  const kind = message.type ?? message.role;
  return kind === "ai" || kind === "AIMessageChunk" || kind === "assistant";
};

/**
 * Whether an AI message carries stamped custom metadata that must reach the
 * rendered bubble (e.g. the used-memory attribution the backend attaches in
 * `ReadOnlyMemoryMiddleware.after_agent`).
 *
 * Such messages are exempt from the duplicate-strip: they keep the streamed
 * message's id, and the runtime's `appendLangChainChunk` fully replaces a
 * same-id message when the incoming one is not a chunk — so the bubble is
 * swapped in place (same content, metadata added) rather than duplicated.
 * @param message - A serialized message from a node output.
 * @returns True when `additional_kwargs.metadata` is a non-empty object.
 */
const carriesStampedMetadata = (message: unknown): boolean => {
  if (!isRecord(message)) return false;
  const additionalKwargs = message.additional_kwargs;
  if (!isRecord(additionalKwargs)) return false;
  const metadata = additionalKwargs.metadata;
  return isRecord(metadata) && Object.keys(metadata).length > 0;
};

/**
 * Strip the duplicate assistant message from `updates` stream events.
 *
 * The v2 chat subscribes with streamMode ["messages","updates","custom"], so a
 * turn's assistant message arrives twice: once as cumulative `messages/partial`
 * token chunks (which build the visible bubble) and again in full inside the
 * `updates` frame emitted by the graph's model node
 * (`{"model":{"messages":[...]}}`). assistant-ui renders the `updates` copy as a
 * SECOND identical bubble — the duplicate-reply bug.
 *
 * We drop ONLY the AI/assistant messages from each `updates` node output, so the
 * duplicate bubble disappears while everything else on the frame is preserved:
 * interrupts (`__interrupt__`), tool/middleware state, and — crucially — any
 * `ToolMessage`/human/system messages a node emits (removing every message would
 * discard real tool results). `values` events are intentionally left untouched:
 * the reconnect/load path yields a synthetic `values` event to hydrate the
 * thread and relies on its `messages`.
 * @param generator - The upstream LangGraph event stream.
 * @yields Each event unchanged, except `updates` frames with their node-output
 *   AI messages removed.
 */
export async function* dedupeUpdatesMessages(
  generator: AsyncGenerator<LangGraphMessagesEvent<LangChainMessage>>
): AsyncGenerator<LangGraphMessagesEvent<LangChainMessage>> {
  for await (const event of generator) {
    if (eventType(event.event) !== "updates" || !isRecord(event.data)) {
      yield event;
      continue;
    }

    let changed = false;
    const nextData: Record<string, unknown> = {};
    for (const [nodeKey, nodeOutput] of Object.entries(event.data)) {
      if (!isRecord(nodeOutput) || !Array.isArray(nodeOutput.messages)) {
        nextData[nodeKey] = nodeOutput;
        continue;
      }

      const kept = nodeOutput.messages.filter(
        (message) => !isAiMessage(message) || carriesStampedMetadata(message)
      );
      if (kept.length === nodeOutput.messages.length) {
        nextData[nodeKey] = nodeOutput;
        continue;
      }

      changed = true;
      if (kept.length === 0) {
        const { messages: _messages, ...rest } = nodeOutput;
        nextData[nodeKey] = rest;
      } else {
        nextData[nodeKey] = { ...nodeOutput, messages: kept };
      }
    }

    yield changed ? { ...event, data: nextData } : event;
  }
}
